import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, Loader2, Plus, ShieldCheck, Trash2, Baby } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

const RELATIONSHIPS = ["Visitor", "Regular Attendee", "Member (RHF)", "Visiting Member"];
const inputCls = "w-full min-h-[48px] rounded-xl border border-border bg-white px-4 text-base outline-none focus:border-water focus:ring-2 focus:ring-water/20 transition-[border-color,box-shadow]";

const emptyChild = {
  full_name: "", preferred_name: "", dob: "", age_grade: "", allergies: "", dietary: "",
  medication: "", support_needs: "", support_helps: "", other_notes: "",
};

const initial = {
  parent_name: "", parent_mobile: "", emergency_contact: "", relationship_to_child: "",
  authorized_pickup: "", not_authorized: "", lfcc_relationship: "",
  children: [{ ...emptyChild }],
  photo_permission: null, medical_consent: null, instructions: "",
};

function Field({ label, required, children, hint, span }) {
  return (
    <label className={`block ${span ? "sm:col-span-2" : ""}`}>
      <span className="block text-sm font-semibold text-river mb-1.5">{label} {required && <span className="text-ember">*</span>}</span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground mt-1.5">{hint}</span>}
    </label>
  );
}

function YesNo({ value, onChange, testid }) {
  return (
    <div className="flex gap-2.5" data-testid={testid}>
      {[[true, "Yes"], [false, "No"]].map(([v, label]) => (
        <button key={label} type="button" data-testid={`${testid}-${label.toLowerCase()}`} onClick={() => onChange(v)}
          className={`min-h-[48px] px-6 rounded-full border text-sm font-semibold transition-colors ${value === v ? "bg-river text-alabaster border-river" : "bg-white border-border hover:border-water"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

export default function ChildrenRegister() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setChild = (i, k, v) =>
    setForm((f) => ({ ...f, children: f.children.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));

  const valid =
    form.parent_name.trim() && form.parent_mobile.trim() &&
    form.children.every((c) => c.full_name.trim()) &&
    form.photo_permission !== null && form.medical_consent !== null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        authorized_pickup: form.authorized_pickup.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/children/register", payload);
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background texture-grain" data-testid="children-register-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-alabaster/85 border-b border-border/60">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="children-brand-link">
            <Logo size={38} />
            <span className="font-serif font-semibold text-river text-lg">LFCC</span>
          </a>
          <a href="/" data-testid="children-back-link" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-river px-3 py-2 rounded-full transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to LFCC
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        {done ? (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="bg-white rounded-3xl border border-border shadow-xl shadow-river/5 p-8 sm:p-12 text-center" data-testid="children-success-card">
            <div className="mx-auto w-16 h-16 rounded-full bg-river flex items-center justify-center mb-6">
              <Baby className="w-7 h-7 text-ember-light" />
            </div>
            <p className="text-xs uppercase tracking-[0.25em] font-mono text-ember mb-3">Registration Received</p>
            <h3 className="font-serif text-3xl sm:text-4xl text-river mb-4">
              {done.children_registered} {done.children_registered === 1 ? "child" : "children"} registered!
            </h3>
            <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
              Our Children's Ministry team has been notified. On Sunday, simply come to the Children's Ministry check-in area — you'll receive a secure pickup code when you check in.
            </p>
            <a href="/" data-testid="children-home-button" className="inline-flex items-center gap-2 mt-8 min-h-[48px] px-7 rounded-full bg-river text-alabaster text-sm font-semibold hover:bg-river-light transition-colors">
              Back to LFCC
            </a>
          </motion.div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.25em] font-mono text-ember mb-3">LFCC Children's Ministry</p>
              <h1 className="font-serif text-4xl sm:text-5xl text-river">Child Registration</h1>
              <p className="text-muted-foreground mt-4 max-w-md mx-auto">
                Register once — after this, Sundays are a quick secure check-in. Everything you share here is visible only to authorized Children's Ministry personnel.
              </p>
              <p className="inline-flex items-center gap-1.5 text-xs text-water font-semibold mt-3">
                <ShieldCheck className="w-4 h-4" /> Restricted access · secure pickup codes
              </p>
            </motion.div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-border p-6" data-testid="parent-section">
                <h3 className="font-serif text-2xl text-river mb-5">Parent / Guardian</h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Parent / Guardian Name" required>
                    <input data-testid="parent-name" className={inputCls} value={form.parent_name} onChange={(e) => set("parent_name", e.target.value)} />
                  </Field>
                  <Field label="Mobile Number" required>
                    <input data-testid="parent-mobile" type="tel" className={inputCls} value={form.parent_mobile} onChange={(e) => set("parent_mobile", e.target.value)} />
                  </Field>
                  <Field label="Secondary / Emergency Contact">
                    <input data-testid="emergency-contact" className={inputCls} placeholder="Name and number" value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} />
                  </Field>
                  <Field label="Relationship to Child">
                    <input data-testid="relationship-to-child" className={inputCls} placeholder="e.g. Mother, Grandmother" value={form.relationship_to_child} onChange={(e) => set("relationship_to_child", e.target.value)} />
                  </Field>
                  <Field label="Person(s) authorized to pick up" span hint="Separate names with commas.">
                    <input data-testid="authorized-pickup" className={inputCls} placeholder="e.g. Michelle Smith, David Smith" value={form.authorized_pickup} onChange={(e) => set("authorized_pickup", e.target.value)} />
                  </Field>
                  <Field label="Person(s) NOT authorized to pick up" span hint="If applicable — confidential.">
                    <input data-testid="not-authorized" className={inputCls} value={form.not_authorized} onChange={(e) => set("not_authorized", e.target.value)} />
                  </Field>
                  <Field label="Your relationship to LFCC" span>
                    <div className="flex flex-wrap gap-2.5" data-testid="lfcc-relationship">
                      {RELATIONSHIPS.map((r) => (
                        <button key={r} type="button" data-testid={`rel-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => set("lfcc_relationship", r)}
                          className={`min-h-[44px] px-4 rounded-full border text-sm font-medium transition-colors ${form.lfcc_relationship === r ? "bg-river text-alabaster border-river" : "bg-white border-border hover:border-water"}`}>
                          {r}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
              </div>

              {form.children.map((child, i) => (
                <div key={i} className="bg-white rounded-2xl border border-border p-6" data-testid={`child-card-${i}`}>
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-serif text-2xl text-river">Child {i + 1}</h3>
                    {form.children.length > 1 && (
                      <button type="button" data-testid={`remove-child-${i}`} onClick={() => set("children", form.children.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Child's Full Name" required>
                      <input data-testid={`child-name-${i}`} className={inputCls} value={child.full_name} onChange={(e) => setChild(i, "full_name", e.target.value)} />
                    </Field>
                    <Field label="Preferred Name">
                      <input data-testid={`child-preferred-${i}`} className={inputCls} value={child.preferred_name} onChange={(e) => setChild(i, "preferred_name", e.target.value)} />
                    </Field>
                    <Field label="Date of Birth">
                      <input data-testid={`child-dob-${i}`} type="date" className={inputCls} value={child.dob} onChange={(e) => setChild(i, "dob", e.target.value)} />
                    </Field>
                    <Field label="Age / Grade">
                      <input data-testid={`child-grade-${i}`} className={inputCls} placeholder="e.g. 7 / Grade 2" value={child.age_grade} onChange={(e) => setChild(i, "age_grade", e.target.value)} />
                    </Field>
                    <Field label="Allergies" span>
                      <input data-testid={`child-allergies-${i}`} className={inputCls} placeholder="Leave blank if none" value={child.allergies} onChange={(e) => setChild(i, "allergies", e.target.value)} />
                    </Field>
                    <Field label="Dietary Restrictions" span>
                      <input data-testid={`child-dietary-${i}`} className={inputCls} placeholder="Leave blank if none" value={child.dietary} onChange={(e) => setChild(i, "dietary", e.target.value)} />
                    </Field>
                    <Field label="Medication / Emergency Considerations" span>
                      <input data-testid={`child-medication-${i}`} className={inputCls} placeholder="Leave blank if none" value={child.medication} onChange={(e) => setChild(i, "medication", e.target.value)} />
                    </Field>
                    <Field label="Support Needs" span hint="Does your child have any disability, medical, sensory, behavioural, communication, mobility or other support needs our team should know about to provide safe and appropriate care?">
                      <textarea data-testid={`child-support-${i}`} rows={3} className={`${inputCls} py-3 resize-none`} value={child.support_needs} onChange={(e) => setChild(i, "support_needs", e.target.value)} />
                    </Field>
                    <Field label="What helps your child feel safe, comfortable and supported?" span>
                      <textarea data-testid={`child-helps-${i}`} rows={3} className={`${inputCls} py-3 resize-none`} value={child.support_helps} onChange={(e) => setChild(i, "support_helps", e.target.value)} />
                    </Field>
                    <Field label="Anything else our team should know?" span>
                      <textarea data-testid={`child-other-${i}`} rows={2} className={`${inputCls} py-3 resize-none`} value={child.other_notes} onChange={(e) => setChild(i, "other_notes", e.target.value)} />
                    </Field>
                  </div>
                </div>
              ))}

              <button type="button" data-testid="add-child-button" onClick={() => set("children", [...form.children, { ...emptyChild }])}
                className="w-full min-h-[52px] rounded-2xl border-2 border-dashed border-border text-sm font-semibold text-river hover:border-water hover:bg-water/5 transition-colors inline-flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" /> Add Another Child
              </button>

              <div className="bg-white rounded-2xl border border-border p-6 space-y-6" data-testid="consent-section">
                <h3 className="font-serif text-2xl text-river">Parent Instructions & Consent</h3>
                <div>
                  <p className="text-sm font-semibold text-river mb-2.5">Photo / media permission <span className="text-ember">*</span></p>
                  <YesNo testid="photo-permission" value={form.photo_permission} onChange={(v) => set("photo_permission", v)} />
                  <p className="text-xs text-muted-foreground mt-2">May your child appear in LFCC photos or media?</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-river mb-2.5">Emergency medical / contact consent <span className="text-ember">*</span></p>
                  <YesNo testid="medical-consent" value={form.medical_consent} onChange={(v) => set("medical_consent", v)} />
                  <p className="text-xs text-muted-foreground mt-2">In an emergency, may our team seek medical assistance and contact you immediately?</p>
                </div>
                <Field label="Any other instructions for the team">
                  <textarea data-testid="parent-instructions" rows={3} className={`${inputCls} py-3 resize-none`} value={form.instructions} onChange={(e) => set("instructions", e.target.value)} />
                </Field>
              </div>

              <button data-testid="children-submit-button" onClick={submit} disabled={!valid || submitting}
                className="w-full min-h-[52px] rounded-full bg-ember text-white font-semibold hover:bg-ember-light disabled:opacity-40 transition-[background-color,transform] active:scale-[0.98] inline-flex items-center justify-center gap-2 shadow-lg shadow-ember/25">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Register {form.children.length > 1 ? `${form.children.length} Children` : "My Child"}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
